import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import { useTranslation } from '@rbx/intl';
import { Grid, Button } from '@rbx/ui';
import Link from 'next/link';
import useCoursesStyles from '@modules/learning/components/Courses.styles';
import learningClient from '@modules/clients/learningClient';
import { coursesPathSuffix } from '@modules/learning/constants/coursesNavConstants';
import { Status } from '@modules/learning/types/learning';
import SaveProgressErrorDialog from '@modules/learning/components/SaveProgressErrorDialog';
import { getLocalLearningProgress } from '@modules/learning/utils/localStorageSync';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsCoursesEvent from '@modules/eventStream/enum/DocsCoursesEvent';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { getCourseLinkForLocale } from '@modules/learning/utils/courseUtils';

export interface LessonNavigationSectionProps {
  next: string | null;
  currentLessonPath?: string;
}

const LessonNavigationSection: FunctionComponent<
  React.PropsWithChildren<LessonNavigationSectionProps>
> = ({ next, currentLessonPath }) => {
  const { translate } = useTranslation();
  const [hasInitializedProgress, setHasInitializedProgress] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { classes } = useCoursesStyles();
  const { targetLocale } = useCurrentLocale();

  useEffect(() => {
    const initializeLessonProgress = async () => {
      if (!currentLessonPath || hasInitializedProgress) {
        return;
      }

      try {
        let currentProgress = getLocalLearningProgress();
        let currentStatus = currentProgress[currentLessonPath];

        if (currentStatus === undefined && Object.keys(currentProgress).length === 0) {
          currentProgress = await learningClient.getLessonProgress();
          currentStatus = currentProgress[currentLessonPath];
        }

        if (currentStatus === undefined || currentStatus === Status.STATUS_ENUM_NOT_STARTED) {
          await learningClient.updateLessonProgress(
            currentLessonPath,
            Status.STATUS_ENUM_IN_PROGRESS,
          );
        }
        setHasInitializedProgress(true);
      } catch (err) {
        console.error('Failed to initialize lesson progress:', err);
        setHasInitializedProgress(true);
      }
    };
    initializeLessonProgress();
  }, [currentLessonPath, hasInitializedProgress, targetLocale]);

  const handleNextClick = async (event: React.MouseEvent) => {
    unifiedLoggerClient.logClickEvent({
      eventName: DocsCoursesEvent.clickLessonComplete,
      parameters: {
        locale: targetLocale ? String(targetLocale) : '',
        lessonPath: currentLessonPath || '',
        nextLessonPath: next || '',
      },
    });
    if (currentLessonPath) {
      event?.preventDefault();
      setIsUpdatingProgress(true);

      try {
        await learningClient.updateLessonProgress(currentLessonPath, Status.STATUS_ENUM_COMPLETED);

        if (!next) {
          unifiedLoggerClient.logClickEvent({
            eventName: DocsCoursesEvent.clickCourseComplete,
            parameters: {
              locale: targetLocale ? String(targetLocale) : '',
              lessonPath: currentLessonPath,
            },
          });
        }
        setIsUpdatingProgress(false);
        linkRef.current?.click();
      } catch (err) {
        console.error(`Failed to update lesson status:`, err);
        setIsUpdatingProgress(false);
        setShowErrorDialog(true);
      }
    }
  };

  const localizedHref = getCourseLinkForLocale(targetLocale, next || coursesPathSuffix);

  const handleContinueAnyway = () => {
    setShowErrorDialog(false);
    linkRef.current?.click();
  };

  const handleCancel = () => {
    setShowErrorDialog(false);
  };

  return (
    <React.Fragment>
      <Grid container className={classes.lessonNavigation}>
        <Link
          // eslint-disable-next-line react/forbid-component-props
          href={localizedHref}
          ref={linkRef}
          className={classes.lessonNavigationLink}>
          <Button
            onClick={handleNextClick}
            variant='contained'
            color='secondary'
            size='medium'
            disabled={isUpdatingProgress}
            className={classes.lessonNavigationButton}>
            {next ? translate('Action.Continue') : translate('Action.Complete')}
          </Button>
        </Link>
      </Grid>

      <SaveProgressErrorDialog
        open={showErrorDialog}
        onContinue={handleContinueAnyway}
        onCancel={handleCancel}
      />
    </React.Fragment>
  );
};

export default LessonNavigationSection;
